import { useTranslation } from 'react-i18next'
import { Head } from 'vite-react-ssg'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, MessageSquare } from 'lucide-react'
import { motion } from 'framer-motion'

export function Impressum() {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  return (
    <>
      <Head>
        <html lang={i18n.language} />
        <title>{isGerman ? 'Impressum' : 'Legal Notice'} | Jonas Fackelmeyer</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-background-secondary border-b border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-secondary hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {isGerman ? 'Zurück zur Startseite' : 'Back to Homepage'}
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-8">
              {isGerman ? 'Impressum' : 'Legal Notice'}
            </h1>

            <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
              {/* Angaben gemäß § 5 DDG */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'Angaben gemäß § 5 DDG' : 'Information according to § 5 DDG'}
                </h2>
                <address className="not-italic text-foreground-secondary space-y-1">
                  <p className="font-medium text-foreground">Jonas Fackelmeyer</p>
                  <p>Am Waldschlößchen 22</p>
                  <p>58791 Werdohl</p>
                  <p>{isGerman ? 'Deutschland' : 'Germany'}</p>
                </address>
              </section>

              {/* Kontakt */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'Kontakt' : 'Contact'}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 text-foreground-secondary">
                    <Mail className="w-5 h-5 text-primary shrink-0 mt-1" />
                    <a
                      href="mailto:jonas@fackelmeyer.com"
                      className="hover:text-primary transition-colors"
                    >
                      jonas@fackelmeyer.com
                    </a>
                  </div>
                  <div className="flex items-start gap-3 text-foreground-secondary">
                    <MessageSquare className="w-5 h-5 text-primary shrink-0 mt-1" />
                    <div>
                      <a
                        href="/#contact"
                        className="hover:text-primary transition-colors"
                      >
                        {isGerman ? 'Kontaktformular' : 'Contact Form'}
                      </a>
                      <p className="text-sm text-foreground-muted mt-1">
                        {isGerman
                          ? 'Schnelle Kontaktaufnahme mit Antwort innerhalb von 24 Stunden'
                          : 'Quick contact with response within 24 hours'}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Berufsbezeichnung */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'Berufsbezeichnung' : 'Professional Title'}
                </h2>
                <p className="text-foreground-secondary">
                  {isGerman
                    ? 'Freiberuflicher Softwareentwickler / IT-Dienstleister'
                    : 'Freelance Software Developer / IT Service Provider'}
                </p>
              </section>

              {/* EU-Streitschlichtung */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'EU-Streitschlichtung' : 'EU Dispute Resolution'}
                </h2>
                <p className="text-foreground-secondary">
                  {isGerman ? (
                    <>
                      Die Europäische Kommission stellt eine Plattform zur
                      Online-Streitbeilegung (OS) bereit:{' '}
                      <a
                        href="https://ec.europa.eu/consumers/odr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        https://ec.europa.eu/consumers/odr/
                      </a>
                    </>
                  ) : (
                    <>
                      The European Commission provides a platform for online
                      dispute resolution (ODR):{' '}
                      <a
                        href="https://ec.europa.eu/consumers/odr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        https://ec.europa.eu/consumers/odr/
                      </a>
                    </>
                  )}
                </p>
              </section>

              {/* Verbraucherstreitbeilegung */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman
                    ? 'Verbraucherstreitbeilegung / Universalschlichtungsstelle'
                    : 'Consumer Dispute Resolution'}
                </h2>
                <p className="text-foreground-secondary">
                  {isGerman
                    ? 'Ich bin nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'
                    : 'I am not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.'}
                </p>
              </section>

              {/* Haftung für Inhalte */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'Haftung für Inhalte' : 'Liability for Content'}
                </h2>
                <p className="text-foreground-secondary">
                  {isGerman
                    ? 'Als Diensteanbieter bin ich gemäß § 7 Abs.1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG bin ich als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werde ich diese Inhalte umgehend entfernen.'
                    : 'As a service provider, I am responsible for my own content on these pages in accordance with general laws pursuant to § 7 para. 1 DDG. According to §§ 8 to 10 DDG, however, I am not obligated as a service provider to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information under general laws remain unaffected. However, liability in this regard is only possible from the time of knowledge of a specific infringement. Upon becoming aware of corresponding infringements, I will remove this content immediately.'}
                </p>
              </section>

              {/* Haftung für Links */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'Haftung für Links' : 'Liability for Links'}
                </h2>
                <p className="text-foreground-secondary">
                  {isGerman
                    ? 'Diese Website enthält Links zu externen Websites Dritter, auf deren Inhalte ich keinen Einfluss habe. Deshalb kann ich für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werde ich derartige Links umgehend entfernen.'
                    : 'This website contains links to external third-party websites over whose content I have no influence. Therefore, I cannot assume any liability for this external content. The respective provider or operator of the pages is always responsible for the content of the linked pages. The linked pages were checked for possible legal violations at the time of linking. Illegal content was not recognizable at the time of linking. However, a permanent content control of the linked pages is not reasonable without concrete evidence of an infringement. Upon becoming aware of legal violations, I will remove such links immediately.'}
                </p>
              </section>

              {/* Urheberrecht */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? 'Urheberrecht' : 'Copyright'}
                </h2>
                <p className="text-foreground-secondary">
                  {isGerman
                    ? 'Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitte ich um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werde ich derartige Inhalte umgehend entfernen.'
                    : 'The content and works created by the site operator on these pages are subject to German copyright law. Duplication, processing, distribution, and any kind of exploitation outside the limits of copyright law require the written consent of the respective author or creator. Downloads and copies of this site are only permitted for private, non-commercial use. Insofar as the content on this site was not created by the operator, the copyrights of third parties are respected. In particular, third-party content is marked as such. Should you nevertheless become aware of a copyright infringement, please notify me accordingly. Upon becoming aware of legal violations, I will remove such content immediately.'}
                </p>
              </section>
            </div>

            {/* Back Link */}
            <div className="mt-12 pt-8 border-t border-border">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                {isGerman ? 'Zurück zur Startseite' : 'Back to Homepage'}
              </Link>
            </div>
          </motion.article>
        </main>
      </div>
    </>
  )
}
